import * as Phaser from "phaser"
import { EventBus } from "../core/event-bus"
import { TEX } from "../core/keys"
import { AUDIO, EVENT } from "../core/keys"
import {
  setCircleBodyInWorldPixels,
  setRectBodyInWorldPixels,
} from "../core/physics-utils"
import { THEME } from "../core/theme"
import {
  GAME_HEIGHT,
  GAME_WIDTH,
  LANE_TOP_Y,
  PROJECTILE_SIZES,
  SCENE_TUNING,
} from "../data/constants"
import {
  ENEMY_DEFINITIONS,
  type EnemyAttackSpec,
  type EnemyDefinition,
  type EnemyMovementSpec,
} from "../data/enemies"
import { PROJECTILE_ANIMATION_BY_TEXTURE } from "../data/fx-animations"
import type { VfxManager } from "./vfx-manager"

import {
  type RuntimeState,
  type RuntimeStateContext,
  StatefulRuntime,
} from "../core/stateful-runtime"
import type {
  BrainBossState,
  DiverPhase,
  DiverState,
  Enemy,
  EnemyData,
  EnemyType,
  GameSprite,
  GathererPhase,
  HealerPhase,
  ProjectileEntityData,
  SnakeFlightPhase,
  SnakePartState,
} from "../types"
import { type FilterableGameObject, applyGlowFilter } from "../ui/neon-fx"
import {
  BOSS_ATTACK_PATTERNS,
  type EnemyBossPhaseHandler,
  createBossMovementHandler,
  createBossPhaseHandler,
  createBrainBossState,
  createSnakePartState,
} from "./boss-system"
import type { BossSystem } from "./boss-system"
import type { BumperSystem } from "./bumper-system"
import type { EconomySystem } from "./economy-system"
import { playSfx } from "./sfx-generator"
import { ToxicZoneSystem } from "./toxic-zone-system"
import { waveGenerator } from "./wave-generator"

const COLOR = THEME.colors

export type EnemyMovementHandler = (
  sprite: GameSprite,
  time: number,
  delta: number,
  playerY: number,
  playerX: number,
  scrollCompensation: number,
) => void

export type EnemyAttackHandler = (context: {
  enemySprite: GameSprite
  playerX: number
  playerY: number
  projectileGroup: Phaser.Physics.Arcade.Group
  vfx: VfxManager
  waveTimer: number
}) => void

export type EnemyRuntime = BaseEnemyRuntime

export type RuntimeEnemyDefinition = Omit<
  EnemyDefinition,
  "movement" | "attack" | "bossPhase"
> & {
  movement: EnemyMovementHandler
  attack?: EnemyAttackHandler
  bossPhase?: EnemyBossPhaseHandler
  movementSpec: EnemyMovementSpec
  attackSpec?: EnemyAttackSpec
}

export const MOVEMENT_DEBUG_LABEL_DATA_KEY = "movementDebugLabel"
const ENEMY_DEFEAT_IN_PROGRESS_DATA_KEY = "enemyDefeatInProgress"

const ENEMY_SPAWN_TUNING = {
  topMargin: 50,
  bottomMargin: 150,
  horizontalSpawnOffset: 50,
  swarmVariance: 2,
  swarmVarianceCenterOffset: 0.5,
} as const

const SHMUP_LANES = [GAME_HEIGHT * 0.24, GAME_HEIGHT * 0.48, GAME_HEIGHT * 0.74]

const MOVEMENT_DEBUG_COLORS: Record<string, string> = {
  DARIUS_CONVOY: "#fde047",
  GRADIUS_SWEEP: "#38bdf8",
  RTYPE_PEEL: "#fb7185",
}

const FIRE_SHOT_SFX_TEXTURES = new Set<string>([
  TEX.PROJ_FIREBALL,
  TEX.PROJ_SPARK,
])

export type OrbDroneMovementPatternLabel =
  | "DARIUS_CONVOY"
  | "GRADIUS_SWEEP"
  | "RTYPE_PEEL"

export function getOrbDroneMovementPatternLabel(
  index = 0,
  waveNumber = 1,
): OrbDroneMovementPatternLabel {
  if (waveNumber !== 1) return "DARIUS_CONVOY"

  const patterns: OrbDroneMovementPatternLabel[] = [
    "DARIUS_CONVOY",
    "GRADIUS_SWEEP",
    "RTYPE_PEEL",
  ]
  return patterns[index % patterns.length]
}

export function emitOrbDroneAttackEffects(
  vfx: Pick<VfxManager, "sparkEmitter" | "emitProjectileTrail">,
  originX: number,
  originY: number,
): void {
  vfx.sparkEmitter.explode(4, originX, originY)
  vfx.emitProjectileTrail(originX, originY, 0x22d3ee, 1)
}

const MOVEMENT_PATTERN_LABEL_GETTERS: Partial<Record<EnemyType, () => string>> =
  {
    orbDrone: getOrbDroneMovementPatternLabel,
  }

const ATTACK_EFFECTS_BY_TYPE: Partial<
  Record<EnemyType, typeof emitOrbDroneAttackEffects>
> = {
  orbDrone: emitOrbDroneAttackEffects,
}

function isMovementDebugEnabled(): boolean {
  if (typeof window === "undefined") return false

  const params = new URLSearchParams(window.location.search)
  return (
    params.get("movementDebug") === "1" ||
    params.get("movementDebug") === "true" ||
    params.get("debug") === "1" ||
    params.get("debug") === "true"
  )
}

function getMovementPatternLabel(enemyType: EnemyType): string {
  return (
    MOVEMENT_PATTERN_LABEL_GETTERS[enemyType]?.() ??
    ENEMY_DEFINITIONS[enemyType].movement.action
  )
}

function getMovementDebugColor(label: string): string {
  return MOVEMENT_DEBUG_COLORS[label] ?? "#fde68a"
}

export function clampEnemyY(y: number): number {
  return Phaser.Math.Clamp(y, ENEMY_SPAWN_TUNING.topMargin, GAME_HEIGHT - 40)
}

function shouldUseAdditiveProjectileBlend(textureKey: string): boolean {
  return (
    textureKey === TEX.PROJ_FIREBALL ||
    textureKey === TEX.PROJ_PLASMA_BOLT ||
    textureKey === TEX.PROJ_NEON_RING ||
    textureKey === TEX.PROJ_SPARK
  )
}

function shouldApplyProjectileGlow(textureKey: string): boolean {
  return (
    textureKey === TEX.PROJ_FIREBALL ||
    textureKey === TEX.PROJ_PLASMA_BOLT ||
    textureKey === TEX.PROJ_NEON_RING ||
    textureKey === TEX.PROJ_SPARK
  )
}

function getEnemyProjectileOrigin(enemySprite: GameSprite): {
  x: number
  y: number
} {
  const baseOrigin = {
    x: enemySprite.x - SCENE_TUNING.enemyProjectileOriginOffsetX,
    y: enemySprite.y,
  }
  const enemyData = enemySprite.entityData

  if (enemyData?.kind !== "enemy" || enemyData.type !== "humanoid") {
    return baseOrigin
  }

  return {
    x: enemySprite.x + 24,
    y: enemySprite.y - enemySprite.displayHeight * 0.06,
  }
}

function getWaveSpawnPoint(
  enemyType: EnemyType,
  wave: number,
  waveProgress: number,
): { x: number; y: number } {
  const threatTier = ENEMY_DEFINITIONS[enemyType].threatTier
  const laneCount = wave <= 3 ? 2 : 3
  const laneIndex =
    (wave + threatTier + Math.floor(waveProgress * 4)) % laneCount
  const laneY = SHMUP_LANES[laneIndex]
  const sweep =
    waveProgress > 0.65 ? (laneIndex - 1) * 18 : (waveProgress - 0.5) * 32

  return {
    x:
      GAME_WIDTH +
      ENEMY_SPAWN_TUNING.horizontalSpawnOffset +
      26 +
      laneIndex * 18,
    y: clampEnemyY(laneY + sweep),
  }
}

function spawnEnemyProjectile(
  enemySprite: GameSprite,
  projectileGroup: Phaser.Physics.Arcade.Group,
  vfx: VfxManager,
  waveTimer: number,
  angle: number,
  damage: number,
  speedMultiplier = 1,
  textureKey: string = TEX.PROJ_FIREBALL,
  muzzleFlashColor: number = THEME.colors.danger,
  projectileGlowColor?: number,
  projectileGlowStrength = 0,
  skipCap = false,
  originOffsetX = 0,
  originOffsetY = 0,
): boolean {
  if (!skipCap && waveTimer < 60000 && projectileGroup.countActive(true) >= 3)
    return false

  const projectileOrigin = getEnemyProjectileOrigin(enemySprite)
  const originX = projectileOrigin.x + originOffsetX
  const originY = projectileOrigin.y + originOffsetY
  const projectile = projectileGroup.get(
    originX,
    originY,
    textureKey,
  ) as GameSprite | null
  if (!projectile) return false

  projectile.setActive(true)
  projectile.setVisible(true)
  projectile.setPosition(originX, originY)
  projectile.anims.stop()
  projectile.setTexture(textureKey)

  const size =
    PROJECTILE_SIZES[textureKey] ?? PROJECTILE_SIZES[TEX.PROJ_FIREBALL]
  projectile.setDisplaySize(size.w, size.h)

  const projectileAnimKey = PROJECTILE_ANIMATION_BY_TEXTURE[textureKey]
  if (projectileAnimKey) {
    projectile.anims.play(projectileAnimKey, true)
  }

  const projectileSpeed = SCENE_TUNING.enemyProjectileSpeed * speedMultiplier
  const vx = Math.cos(angle) * projectileSpeed
  const vy = Math.sin(angle) * projectileSpeed

  projectile.entityData = {
    kind: "projectile",
    vx,
    vy,
    damage,
    pierce: 0,
    isEnemy: true,
    bounceCount: undefined,
    x: originX,
    y: originY,
    color: `#${muzzleFlashColor.toString(16).padStart(6, "0")}`,
    size: 6,
  } satisfies ProjectileEntityData

  projectile.setRotation(angle)
  projectile.setBlendMode(
    shouldUseAdditiveProjectileBlend(textureKey)
      ? Phaser.BlendModes.ADD
      : Phaser.BlendModes.NORMAL,
  )
  if (textureKey === TEX.PROJ_SPARK) {
    vfx.emitProjectileTrail(
      originX,
      originY,
      projectileGlowColor ?? 0xfacc15,
      1.4,
    )
    vfx.sparkEmitter.explode(3, originX, originY)
  }
  if (textureKey === TEX.PROJ_MISSILE) {
    vfx.emitProjectileTrail(originX, originY, 0xf97316, 1.2)
    vfx.sparkEmitter.explode(2, originX, originY)
  }
  vfx.emitMuzzleFlash(originX, originY, angle, muzzleFlashColor, 0.65)

  if (
    projectileGlowColor !== undefined &&
    shouldApplyProjectileGlow(textureKey)
  ) {
    const filterTarget = projectile as unknown as FilterableGameObject
    filterTarget.enableFilters()
    filterTarget.filters?.internal.clear()
    applyGlowFilter(filterTarget, {
      color: projectileGlowColor,
      outerStrength: projectileGlowStrength > 0 ? projectileGlowStrength : 5,
      innerStrength: 0.5,
      quality: 8,
      distance: 8,
    })
  }

  const body = projectile.body as Phaser.Physics.Arcade.Body
  if (body) {
    body.setAllowGravity(false)
    body.setCircle(3, 2, 2)
    body.setVelocity(vx * 60, vy * 60)
  }

  return true
}

function playFireShotSfx(textureKey: string, amount = 1): void {
  if (FIRE_SHOT_SFX_TEXTURES.has(textureKey)) {
    playSfx(AUDIO.SFX_FIRE_SHOT, amount)
  }
}

function createEnemyMovement(
  spec: EnemyMovementSpec,
  _hpBase: number,
): EnemyMovementHandler {
  switch (spec.action) {
    case "track-player-y":
      return () => {}

    case "chase-player":
      return () => {}

    case "panic-or-sine":
      return () => {}

    case "sine":
      return () => {}

    case "stationary-mine":
      return () => {}

    case "router-movement":
      return () => {}

    case "router-star-bob":
      return () => {}

    case "harvester-patrol":
      return () => {}

    case "hovering":
      return () => {}

    case "moai-drift":
      return () => {}

    case "piranha-strike":
      return () => {}

    case "hornet-pattern":
      return () => {}

    case "mine-climb":
      return () => {}
  }

  const bossHandler = createBossMovementHandler(spec)
  if (bossHandler) return bossHandler

  return (sprite) => {
    const ed = sprite.entityData as EnemyData
    sprite.x += ed.vx
    sprite.y += ed.vy
  }
}

function createEnemyAttack(
  spec?: EnemyAttackSpec,
): EnemyAttackHandler | undefined {
  if (!spec) return undefined

  switch (spec.action) {
    case "aimed-spread-shot":
      return ({
        enemySprite,
        playerX,
        playerY,
        projectileGroup,
        vfx,
        waveTimer,
      }) => {
        const ed = enemySprite.entityData as EnemyData
        const isBoss =
          ed?.type === "snake" || ed?.type === "brain" || ed?.isBossPart

        if (isBoss) {
          const patternKey = ed?.bossAttackPattern ?? "opening"
          const pattern = BOSS_ATTACK_PATTERNS[patternKey]

          if (patternKey === "spiral") {
            const spiralAngle = (waveTimer / 400) % (Math.PI * 2)
            spawnEnemyProjectile(
              enemySprite,
              projectileGroup,
              vfx,
              waveTimer,
              spiralAngle,
              Math.max(4, Math.round(spec.damage * pattern.damageMultiplier)),
              pattern.speedMultiplier,
            )
            return
          }

          const baseAngle = Math.PI // Fixed straight left
          const spreadAngles: number[] = []
          if (pattern.projectileCount === 1) {
            spreadAngles.push(0)
          } else if (pattern.projectileCount > 1) {
            for (let i = 0; i < pattern.projectileCount; i += 1) {
              const t = (i / (pattern.projectileCount - 1)) * 2 - 1
              spreadAngles.push(t * pattern.spreadDeg)
            }
          }

          for (const spread of spreadAngles) {
            spawnEnemyProjectile(
              enemySprite,
              projectileGroup,
              vfx,
              waveTimer,
              baseAngle + Phaser.Math.DegToRad(spread),
              Math.max(4, Math.round(spec.damage * pattern.damageMultiplier)),
              pattern.speedMultiplier,
            )
          }

          return
        }

        const originX =
          enemySprite.x - SCENE_TUNING.enemyProjectileOriginOffsetX
        const originY = enemySprite.y
        const aimedSpreadAngleToPlayer = Phaser.Math.Angle.Between(
          enemySprite.x,
          enemySprite.y,
          playerX,
          playerY,
        )
        const projectileAngleToPlayer = Phaser.Math.Angle.Between(
          originX,
          originY,
          playerX,
          playerY,
        )
        const projectileSpeedMultiplier = spec.projectileSpeedMultiplier ?? 1
        const spreadAngles: number[] = []

        ATTACK_EFFECTS_BY_TYPE[ed?.type]?.(vfx, originX, originY)

        if ((spec.projectileCount ?? 1) === 1) {
          spreadAngles.push(0)
        } else {
          const count = spec.projectileCount ?? 1
          const spread = spec.spreadDeg ?? 0
          for (let i = 0; i < count; i += 1) {
            const t = (i / (count - 1)) * 2 - 1
            spreadAngles.push(t * spread)
          }
        }

        let didFire = false
        for (const spread of spreadAngles) {
          didFire =
            spawnEnemyProjectile(
              enemySprite,
              projectileGroup,
              vfx,
              waveTimer,
              projectileAngleToPlayer + Phaser.Math.DegToRad(spread),
              spec.damage,
              projectileSpeedMultiplier,
            ) || didFire
        }
        if (didFire) {
          playFireShotSfx(TEX.PROJ_FIREBALL, spreadAngles.length > 1 ? 1.15 : 1)
        }
      }
    case "fixed-spread-shot":
      return ({ enemySprite, projectileGroup, vfx, waveTimer }) => {
        const ed = enemySprite.entityData as EnemyData

        ATTACK_EFFECTS_BY_TYPE[ed?.type]?.(vfx, enemySprite.x, enemySprite.y)

        const baseAngle = Math.PI
        const count = spec.projectileCount ?? 1
        const spread = spec.spreadDeg ?? 0
        const speedMult = spec.projectileSpeedMultiplier ?? 1
        const textureKey = spec.projectileTexture ?? TEX.PROJ_FIREBALL
        const muzzleFlashColor = spec.muzzleFlashColor ?? THEME.colors.danger
        const projectileGlowColor = spec.projectileGlowColor
        const projectileGlowStrength = spec.projectileGlowStrength ?? 0
        const skipProjectileCap = ed?.type === "orbDrone"

        if (ed?.type === "humanoid" && ed.humanoidHadoukenBurst) {
          ed.humanoidHadoukenBurst = false
          for (const spread of [-10, 0, 10]) {
            spawnEnemyProjectile(
              enemySprite,
              projectileGroup,
              vfx,
              waveTimer,
              baseAngle + Phaser.Math.DegToRad(spread),
              Math.max(2, Math.ceil(spec.damage * 0.55)),
              speedMult * 0.95,
              TEX.PROJ_FIREBALL,
              0xfacc15,
              0xf97316,
              9,
              true,
            )
          }
          return
        }

        let didFire = false
        if (count === 1) {
          didFire = spawnEnemyProjectile(
            enemySprite,
            projectileGroup,
            vfx,
            waveTimer,
            baseAngle,
            spec.damage,
            speedMult,
            textureKey,
            muzzleFlashColor,
            projectileGlowColor,
            projectileGlowStrength,
            skipProjectileCap,
          )
        } else {
          for (let i = 0; i < count; i++) {
            const t = count === 1 ? 0 : (i / (count - 1)) * 2 - 1
            const angle = baseAngle + Phaser.Math.DegToRad(t * spread)
            didFire =
              spawnEnemyProjectile(
                enemySprite,
                projectileGroup,
                vfx,
                waveTimer,
                angle,
                spec.damage,
                speedMult,
                textureKey,
                muzzleFlashColor,
                projectileGlowColor,
                projectileGlowStrength,
                skipProjectileCap,
              ) || didFire
          }
        }
        if (didFire) {
          playFireShotSfx(textureKey, count > 1 ? 1.15 : 1.05)
        }
      }
    case "moai-ring-shot":
      return ({ enemySprite, projectileGroup, vfx, waveTimer }) => {
        spawnEnemyProjectile(
          enemySprite,
          projectileGroup,
          vfx,
          waveTimer,
          Math.PI,
          spec.damage,
          spec.projectileSpeed / 6.5,
          TEX.PROJ_NEON_RING,
          0x38bdf8,
          0x38bdf8,
          10,
        )
      }
    case "mine-drop":
      return ({ enemySprite, projectileGroup, vfx, waveTimer }) => {
        enemySprite.setTexture(TEX.ENEMY_MINE_DRONE_OPEN)
        for (let i = 0; i < spec.mineCount; i++) {
          const t = spec.mineCount <= 1 ? 0 : (i / (spec.mineCount - 1)) * 2 - 1
          spawnEnemyProjectile(
            enemySprite,
            projectileGroup,
            vfx,
            waveTimer,
            Phaser.Math.DegToRad(125 + t * 18),
            spec.damage,
            spec.mineSpeed,
            TEX.PROJ_MISSILE,
            0xf97316,
            0xf97316,
            8,
            false,
            -8,
            t * spec.mineSpreadY * 0.5,
          )
        }
      }
    case "turret-tracking-shot":
      return ({
        enemySprite,
        playerX,
        playerY,
        projectileGroup,
        vfx,
        waveTimer,
      }) => {
        const angleToPlayer = Phaser.Math.Angle.Between(
          enemySprite.x,
          enemySprite.y,
          playerX,
          playerY,
        )
        const isPlayerInFront = enemySprite.x > playerX
        if (!isPlayerInFront) return

        spawnEnemyProjectile(
          enemySprite,
          projectileGroup,
          vfx,
          waveTimer,
          angleToPlayer,
          spec.damage,
          spec.projectileSpeed / 6.5,
          TEX.PROJ_PLASMA_BOLT,
          0xf472b6,
          0xf472b6,
          6,
        )
      }
    case "aimed-laser-burst":
      return ({
        enemySprite,
        playerX,
        playerY,
        projectileGroup,
        vfx,
        waveTimer,
      }) => {
        const angleToPlayer = Phaser.Math.Angle.Between(
          enemySprite.x,
          enemySprite.y,
          playerX,
          playerY,
        )
        spawnEnemyProjectile(
          enemySprite,
          projectileGroup,
          vfx,
          waveTimer,
          angleToPlayer,
          spec.damage,
          1.2,
          TEX.PROJ_LASER,
          0x4ade80,
          0x4ade80,
          4,
        )
      }
    case "fireball-shot":
      return ({
        enemySprite,
        playerX,
        playerY,
        projectileGroup,
        vfx,
        waveTimer,
      }) => {
        const fireballAngleToPlayer = Phaser.Math.Angle.Between(
          enemySprite.x,
          enemySprite.y,
          playerX,
          playerY,
        )
        const projectileSpeedMultiplier = spec.projectileSpeedMultiplier ?? 1
        const textureKey = spec.projectileTexture ?? TEX.PROJ_FIREBALL
        const muzzleFlashColor = spec.muzzleFlashColor ?? THEME.neon.green
        const projectileGlowColor = spec.projectileGlowColor ?? THEME.neon.green
        const projectileGlowStrength = spec.projectileGlowStrength ?? 7

        const didFire = spawnEnemyProjectile(
          enemySprite,
          projectileGroup,
          vfx,
          waveTimer,
          fireballAngleToPlayer,
          spec.damage,
          projectileSpeedMultiplier,
          textureKey,
          muzzleFlashColor,
          projectileGlowColor,
          projectileGlowStrength,
        )
        if (didFire) {
          playFireShotSfx(textureKey, 1.08)
        }
      }

    case "router-4way":
      return ({ enemySprite, projectileGroup, vfx, waveTimer }) => {
        const cx = GAME_WIDTH / 2
        const cy = GAME_HEIGHT / 2
        const ddx = cx - enemySprite.x
        const ddy = cy - enemySprite.y
        if (ddx * ddx + ddy * ddy > 60 * 60) return

        const baseRotation = enemySprite.angle
        let didFire = false
        for (const offset of [0, 90, 180, 270]) {
          didFire =
            spawnEnemyProjectile(
              enemySprite,
              projectileGroup,
              vfx,
              waveTimer,
              Phaser.Math.DegToRad(offset + baseRotation),
              spec.damage,
              1.0,
              TEX.PROJ_FIREBALL,
              THEME.colors.danger,
              undefined,
              0,
              true,
            ) || didFire
        }
        if (didFire) {
          playFireShotSfx(TEX.PROJ_FIREBALL, 1.2)
        }

        vfx.emitMuzzleFlash(
          enemySprite.x,
          enemySprite.y,
          Phaser.Math.DegToRad(baseRotation),
          0x8b5cf6,
        )

        const ed = enemySprite.entityData as EnemyData
        ed.routerRotation = ((ed.routerRotation ?? 0) + 45) % 90
      }

    case "router-star-burst": {
      const burstSpec = spec as {
        damage: number
        patternCycle: string
        volleyCount: number
        burstDelayMs: number
        projectileSpeedMultiplier?: number
      }
      return ({ enemySprite, projectileGroup, vfx, waveTimer }) => {
        const ed = enemySprite.entityData as EnemyData
        const patternIndex = ed.routerPatternIndex ?? 0
        const isPlusPattern = patternIndex % 2 === 0
        const angles = isPlusPattern ? [0, 90, 180, 270] : [45, 135, 225, 315]

        let didFire = false
        for (let i = 0; i < burstSpec.volleyCount; i++) {
          const angleDeg = angles[i]
          didFire =
            spawnEnemyProjectile(
              enemySprite,
              projectileGroup,
              vfx,
              waveTimer,
              Phaser.Math.DegToRad(angleDeg),
              burstSpec.damage,
              burstSpec.projectileSpeedMultiplier ?? 0.72,
              TEX.PROJ_FIREBALL,
              THEME.colors.danger,
              undefined,
              0,
              true,
            ) || didFire
        }
        if (didFire) {
          playFireShotSfx(TEX.PROJ_FIREBALL, 1.0)
        }

        ed.routerPatternIndex = (patternIndex + 1) % 2
      }
    }
  }
}

function createRuntimeEnemyDefinition(
  definition: EnemyDefinition,
): RuntimeEnemyDefinition {
  return {
    ...definition,
    movement: createEnemyMovement(definition.movement, definition.hpBase),
    attack: createEnemyAttack(definition.attack),
    bossPhase: createBossPhaseHandler(definition.bossPhase),
    movementSpec: definition.movement,
    attackSpec: definition.attack,
  }
}

let _RUNTIME_ENEMY_DEFINITIONS: Record<
  EnemyType,
  RuntimeEnemyDefinition
> | null = null

export function getRuntimeEnemyDefinitions(): Record<
  EnemyType,
  RuntimeEnemyDefinition
> {
  if (!_RUNTIME_ENEMY_DEFINITIONS) {
    _RUNTIME_ENEMY_DEFINITIONS = Object.fromEntries(
      Object.entries(ENEMY_DEFINITIONS).map(([type, definition]) => [
        type,
        createRuntimeEnemyDefinition(definition),
      ]),
    ) as Record<EnemyType, RuntimeEnemyDefinition>
  }
  return _RUNTIME_ENEMY_DEFINITIONS
}

export const RUNTIME_ENEMY_DEFINITIONS: Record<
  EnemyType,
  RuntimeEnemyDefinition
> = new Proxy({} as Record<EnemyType, RuntimeEnemyDefinition>, {
  get(_target, prop) {
    return getRuntimeEnemyDefinitions()[prop as EnemyType]
  },
})

const generateId = () =>
  Date.now().toString(36) + Math.random().toString(36).slice(2, 7)

export interface SpawnWaveEnemyEntryOptions {
  enemyType: EnemyType
  baseX: number
  baseY: number
  remainingSlots: number
  difficultyMultiplier: number
  waveNumber: number
  source: "encounter" | "fallback" | "batch"
  createEnemyByType: (
    type: EnemyType,
    x: number,
    y: number,
    wave: number,
    difficultyMultiplier?: number,
  ) => Enemy
  spawnConfiguredEnemy: (
    enemyType: EnemyType,
    x: number,
    y: number,
    overrides?: Partial<EnemyData>,
  ) => GameSprite | null
  spawnStandardEnemy: (
    enemyType: EnemyType,
    x: number,
    y: number,
    difficultyMultiplier: number,
    source: "encounter" | "fallback" | "batch",
  ) => number
  observeSpawn: (
    sprite: Enemy | GameSprite,
    source: "encounter" | "fallback" | "batch",
  ) => void
}

export function spawnWaveEnemyEntry(
  options: SpawnWaveEnemyEntryOptions,
): number {
  if (options.remainingSlots <= 0) return 0

  return options.spawnStandardEnemy(
    options.enemyType,
    options.baseX,
    options.baseY,
    options.difficultyMultiplier,
    options.source,
  )
}

function createEnemyByType(
  type: EnemyType,
  x: number,
  y: number,
  _wave: number,
  difficultyMultiplier = 1,
): Enemy {
  const def = ENEMY_DEFINITIONS[type]
  const spawnY = type === "mineDrone" ? LANE_TOP_Y : y
  const hp =
    def.hpBase * Math.max(0.92, 0.92 + (difficultyMultiplier - 1) * 0.62)
  const damage =
    def.damage * Math.max(0.88, 0.88 + (difficultyMultiplier - 1) * 0.52)
  const points = def.points * Math.max(1, 1 + (difficultyMultiplier - 1) * 0.22)

  return {
    id: generateId(),
    x,
    y: spawnY,
    lastShot: 0,
    type: def.type,
    vx: def.vx,
    vy: def.vy,
    hp,
    damage,
    points,
    shootCooldown: def.shootCooldown,
  }
}

function spawnEnemyForWave(
  wave: number,
  gameWidth: number,
  gameHeight: number,
  waveProgress = 0.5,
  difficultyMultiplier = 1,
): Enemy {
  const enemyType = waveGenerator.rollEnemyType(wave, waveProgress)
  const spawnPoint = getWaveSpawnPoint(enemyType, wave, waveProgress)
  const spawnX = Math.min(gameWidth + 180, spawnPoint.x)
  const spawnY =
    enemyType === "mineDrone"
      ? LANE_TOP_Y
      : Phaser.Math.Clamp(
          spawnPoint.y + (Math.random() - 0.5) * 28,
          ENEMY_SPAWN_TUNING.topMargin,
          gameHeight - ENEMY_SPAWN_TUNING.bottomMargin,
        )
  return createEnemyByType(
    enemyType,
    spawnX,
    spawnY,
    wave,
    difficultyMultiplier,
  )
}

export class EnemySystem {
  private scene: Phaser.Scene
  private enemyGroup: Phaser.Physics.Arcade.Group
  private projectileGroup: Phaser.Physics.Arcade.Group
  private vfx: VfxManager
  private readonly getDifficultyMultiplier: () => number
  private readonly createRuntime: (
    sprite: GameSprite,
    enemyData: EnemyData,
    movement: EnemyMovementHandler,
  ) => BaseEnemyRuntime
  public waveTimer = 0

  constructor(
    scene: Phaser.Scene,
    enemyGroup: Phaser.Physics.Arcade.Group,
    projectileGroup: Phaser.Physics.Arcade.Group,
    vfx: VfxManager,
    getDifficultyMultiplier: () => number,
    createRuntime?: (
      sprite: GameSprite,
      enemyData: EnemyData,
      movement: EnemyMovementHandler,
    ) => BaseEnemyRuntime,
  ) {
    this.scene = scene
    this.enemyGroup = enemyGroup
    this.projectileGroup = projectileGroup
    this.vfx = vfx
    this.getDifficultyMultiplier = getDifficultyMultiplier
    this.createRuntime = createRuntime ?? this.defaultCreateRuntime
  }

  private defaultCreateRuntime(
    sprite: GameSprite,
    enemyData: EnemyData,
    movement: EnemyMovementHandler,
  ): BaseEnemyRuntime {
    const { createEnemy } = require("./enemies/enemy-registry")
    return createEnemy(sprite, enemyData, movement)
  }

  public hasActiveRouter(): boolean {
    return this.enemyGroup.getChildren().some((sprite) => {
      const data = (sprite as GameSprite).entityData as
        | import("../types").EnemyData
        | undefined
      return data?.type === "router"
    })
  }

  public hasHealableEnemies(): boolean {
    return this.enemyGroup.getChildren().some((sprite) => {
      const data = (sprite as GameSprite).entityData as
        | import("../types").EnemyData
        | undefined
      return (
        sprite.active &&
        data?.kind === "enemy" &&
        data.type !== "healer" &&
        !data.isBossPart
      )
    })
  }

  public spawnEnemy(
    waveOrEnemy: number | Enemy,
    waveProgress?: number,
  ): Enemy | null {
    let enemy =
      typeof waveOrEnemy === "number"
        ? spawnEnemyForWave(
            waveOrEnemy,
            GAME_WIDTH,
            GAME_HEIGHT,
            waveProgress ?? 0.5,
            this.getDifficultyMultiplier(),
          )
        : waveOrEnemy

    if (enemy.type === "healer" && !this.hasHealableEnemies()) {
      enemy = createEnemyByType(
        "orbDrone",
        enemy.x,
        enemy.y,
        typeof waveOrEnemy === "number" ? waveOrEnemy : 1,
        this.getDifficultyMultiplier(),
      )
    }

    const def = getRuntimeEnemyDefinitions()[enemy.type]
    const entityData: EnemyData = {
      kind: "enemy",
      id: enemy.id,
      type: enemy.type,
      vx: enemy.vx,
      vy: enemy.vy,
      hp: enemy.hp,
      maxHp: enemy.hp,
      damage: enemy.damage,
      points: enemy.points,
      shootCooldown: enemy.shootCooldown,
      lastShot: 0,
      x: enemy.x,
      y: enemy.y,
      homeX: enemy.x,
      homeY: enemy.y,
      ballInteraction: def.ballInteraction,
      movementPatternLabel: getMovementPatternLabel(enemy.type),
    }

    entityData.supportState = this.createSupportState(enemy.type)

    if (def.hoverDurationMs) {
      entityData.hoverTimer = def.hoverDurationMs
      entityData.hoverVx = entityData.vx
      entityData.vx = 0
    }

    const sprite = this.spawnEnemySprite(enemy.x, enemy.y, def, entityData, {
      introScale: SCENE_TUNING.enemySpawnIntroScale,
      fadeIn: true,
      tweenDurationMs: SCENE_TUNING.enemySpawnTweenDurationMs,
      depth: 70,
    })
    if (!sprite) return null

    if (def.parts) {
      const parentId = entityData.id
      for (const part of def.parts) {
        this.spawnConfiguredEnemy(
          part.type,
          enemy.x + part.offsetX,
          enemy.y + part.offsetY,
          {
            parentId,
            isBossPart: true,
            partOffsetX: part.offsetX,
            partOffsetY: part.offsetY,
          },
        )
      }
    }

    return enemy
  }

  public spawnConfiguredEnemy(
    enemyType: EnemyType,
    x: number,
    y: number,
    overrides: Partial<EnemyData> = {},
    options: {
      introScale?: number
      fadeIn?: boolean
      tweenDurationMs?: number
      depth?: number
    } = {},
  ): GameSprite | null {
    const def = RUNTIME_ENEMY_DEFINITIONS[enemyType]
    const spawnY = enemyType === "mineDrone" ? LANE_TOP_Y : y
    const entityData: EnemyData = {
      kind: "enemy",
      id: overrides.id ?? generateId(),
      type: enemyType,
      vx: overrides.vx ?? def.vx,
      vy: overrides.vy ?? def.vy,
      hp: overrides.hp ?? def.hpBase,
      maxHp: overrides.maxHp ?? overrides.hp ?? def.hpBase,
      damage: overrides.damage ?? def.damage,
      points: overrides.points ?? def.points,
      shootCooldown: overrides.shootCooldown ?? def.shootCooldown,
      lastShot: overrides.lastShot ?? 0,
      x: overrides.x ?? x,
      y: enemyType === "mineDrone" ? spawnY : (overrides.y ?? y),
      homeX: overrides.homeX ?? x,
      homeY: enemyType === "mineDrone" ? spawnY : (overrides.homeY ?? y),
      movementPatternLabel:
        overrides.movementPatternLabel ?? getMovementPatternLabel(enemyType),
      arcadeMovementPattern: overrides.arcadeMovementPattern,
      convoyStopX: overrides.convoyStopX,
      convoyIndex: overrides.convoyIndex,
      convoyShotDelayMs: overrides.convoyShotDelayMs,
      convoyPatternTimer: overrides.convoyPatternTimer,
      convoyPatternPhase: overrides.convoyPatternPhase,
      convoyShiftTargetY: overrides.convoyShiftTargetY,
      convoyShiftCount: overrides.convoyShiftCount,
      convoyLoopDirection: overrides.convoyLoopDirection,
      convoyReentryY: overrides.convoyReentryY,
      ballInteraction: overrides.ballInteraction ?? def.ballInteraction,
      supportState:
        overrides.supportState ?? this.createSupportState(enemyType),
      hoverTimer: overrides.hoverTimer,
      hoverVx: overrides.hoverVx,
      bossPhase: overrides.bossPhase,
      bossMaxHp: overrides.bossMaxHp,
      bossWave: overrides.bossWave,
      lastDamageReceived: overrides.lastDamageReceived,
      supportGateActive: overrides.supportGateActive,
      parentId: overrides.parentId,
      isBossPart: overrides.isBossPart,
      partOffsetX: overrides.partOffsetX,
      partOffsetY: overrides.partOffsetY,
      bossEntrancing: overrides.bossEntrancing,
      edgeSide: overrides.edgeSide,
    }

    const sprite = this.spawnEnemySprite(x, spawnY, def, entityData, {
      introScale: options.introScale ?? (overrides.isBossPart ? 0.6 : 1),
      fadeIn: options.fadeIn ?? false,
      tweenDurationMs: options.tweenDurationMs ?? 0,
      depth: options.depth ?? (overrides.isBossPart ? 75 : 70),
    })

    if (sprite && def.parts && !overrides.isBossPart) {
      const parentId = entityData.id
      for (const part of def.parts) {
        this.spawnConfiguredEnemy(
          part.type,
          x + part.offsetX,
          enemyType === "mineDrone"
            ? LANE_TOP_Y + part.offsetY
            : y + part.offsetY,
          {
            parentId,
            isBossPart: true,
            partOffsetX: part.offsetX,
            partOffsetY: part.offsetY,
          },
        )
      }
    }

    return sprite
  }

  public createEnemyByType(
    type: EnemyType,
    x: number,
    y: number,
    wave: number,
    difficultyMultiplier = 1,
  ): Enemy {
    return createEnemyByType(type, x, y, wave, difficultyMultiplier)
  }

  public fireEnemyProjectile(
    enemySprite: GameSprite,
    playerX: number,
    playerY: number,
  ): void {
    const ed = enemySprite.entityData
    if (!ed || ed.kind !== "enemy") return

    const def = RUNTIME_ENEMY_DEFINITIONS[ed.type]
    if (!def.attack) return

    const enemyRuntime = enemySprite.getData("enemyRuntime") as
      | EnemyRuntime
      | undefined
    if (enemyRuntime && !enemyRuntime.canFire()) return

    def.attack({
      enemySprite,
      playerX,
      playerY,
      projectileGroup: this.projectileGroup,
      vfx: this.vfx,
      waveTimer: this.waveTimer,
    })

    enemyRuntime?.onFire()
  }

  private configureEnemyBody(sprite: GameSprite): void {
    const body = sprite.body as Phaser.Physics.Arcade.Body
    const ed = sprite.entityData
    if (!body || !ed || ed.kind !== "enemy") return

    body.setAllowGravity(false)

    const def = RUNTIME_ENEMY_DEFINITIONS[ed.type]
    const scale = 1
    if (def.body.shape === "circle") {
      setCircleBodyInWorldPixels(
        sprite,
        def.body.radius * scale,
        def.body.offsetX * scale,
        def.body.offsetY * scale,
      )
    } else {
      setRectBodyInWorldPixels(
        sprite,
        def.body.width * scale,
        def.body.height * scale,
        def.body.offsetX * scale,
        def.body.offsetY * scale,
      )
    }
  }

  private createSupportState(enemyType: EnemyType): EnemyData["supportState"] {
    if (
      enemyType === "snakeHead" ||
      enemyType === "snakeBody" ||
      enemyType === "snakeTail"
    ) {
      return createSnakePartState(
        enemyType === "snakeHead"
          ? "head"
          : enemyType === "snakeBody"
            ? "body"
            : "tail",
      )
    }
    if (enemyType === "brain") {
      return createBrainBossState()
    }
    return undefined
  }

  private spawnEnemySprite(
    x: number,
    y: number,
    def: RuntimeEnemyDefinition,
    entityData: EnemyData,
    options: {
      introScale: number
      fadeIn: boolean
      tweenDurationMs: number
      depth: number
    },
  ): GameSprite | null {
    const sprite = this.enemyGroup.get(x, y, def.texture) as GameSprite | null
    if (!sprite) return null

    sprite.setActive(true)
    sprite.setVisible(true)
    sprite.setAlpha(options.fadeIn ? 0 : 1)
    sprite.setDepth(options.depth)
    sprite.setPosition(x, y)
    sprite.setTexture(def.texture)
    sprite.entityData = entityData
    sprite.setData("id", entityData.id)

    const size = def.spriteSize
    const displayScale = SCENE_TUNING.enemyDisplayScale
    const finalW = size.w * displayScale
    const finalH = size.h * displayScale
    sprite.setDisplaySize(finalW, finalH)
    this.configureEnemyBody(sprite)
    if (options.introScale !== 1) {
      sprite.setDisplaySize(
        finalW * options.introScale,
        finalH * options.introScale,
      )
    }

    if (def.animation && this.scene.anims.exists(def.animation.key)) {
      const animation = this.scene.anims.get(def.animation.key)
      if (animation?.frames?.length) {
        sprite.play(def.animation.key)
      }
    }

    const enemyRuntime = this.createRuntime(sprite, entityData, def.movement)
    if (enemyRuntime) {
      enemyRuntime.onSpawn()
      sprite.setData("enemyRuntime", enemyRuntime)
      sprite.once(Phaser.GameObjects.Events.DESTROY, () => {
        enemyRuntime.destroy()
      })
    }

    if (isMovementDebugEnabled()) {
      const movementLabel =
        entityData.movementPatternLabel ?? def.movementSummary
      const label = this.scene.add
        .text(x, y, movementLabel, {
          fontSize: "9px",
          fontFamily: "monospace",
          color: getMovementDebugColor(movementLabel),
          stroke: "#111827",
          strokeThickness: 3,
        })
        .setOrigin(0.5, 1)
        .setDepth(options.depth + 30)
      sprite.setData(MOVEMENT_DEBUG_LABEL_DATA_KEY, label)
      sprite.once(Phaser.GameObjects.Events.DESTROY, () => {
        label.destroy()
      })
    }

    if (def.threatTier === 4 && !def.bossPhase) {
      Phaser.Actions.AddEffectShine(sprite, {
        duration: 400,
        direction: 0,
        colorFactor: [2.5, 0.8, 0.2, 1],
      })
    }

    if (options.fadeIn) {
      this.scene.tweens.add({
        targets: sprite,
        displayWidth: size.w * displayScale,
        displayHeight: size.h * displayScale,
        alpha: 1,
        duration: options.tweenDurationMs,
        ease: "Quad.easeOut",
      })
    }

    if (!def.assetPath.endsWith("sheet.webp")) {
      sprite.setAngle(0)
    }

    return sprite
  }
}

interface EnemyLoopOptions {
  scene: Phaser.Scene
  enemyGroup: Phaser.Physics.Arcade.Group
  expCoinGroup: Phaser.Physics.Arcade.Group
  enemies: EnemySystem
  vfx: VfxManager
  bossSystem: BossSystem
  getGameData: () => import("../types").GameData
  getDogSprite: () => GameSprite
}

export class EnemyLoop {
  private readonly options: EnemyLoopOptions

  public constructor(options: EnemyLoopOptions) {
    this.options = options
  }

  public update(time: number, delta: number): void {
    const gameData = this.options.getGameData()
    const dogSprite = this.options.getDogSprite()

    for (const child of this.options.enemyGroup.children) {
      const sprite = child as GameSprite
      const enemyData = sprite.entityData
      if (!sprite.active || !enemyData || enemyData.kind !== "enemy") continue

      if (
        !this.updatePosition(
          sprite,
          enemyData,
          time,
          delta,
          gameData,
          dogSprite,
        )
      ) {
        continue
      }

      this.options.bossSystem.updateBossState(sprite, enemyData, time)
      this.updateCombat(sprite, enemyData, delta, dogSprite)
      this.applyVisuals(sprite, enemyData, time)
      this.updateMovementDebugLabel(sprite, enemyData)
    }
  }

  private updatePosition(
    sprite: GameSprite,
    enemyData: EnemyData,
    time: number,
    delta: number,
    gameData: import("../types").GameData,
    dogSprite: GameSprite,
  ): boolean {
    const isBoss =
      enemyData.type === "brain" ||
      enemyData.isBossPart ||
      enemyData.type === "snake" ||
      enemyData.type === "snakeHead" ||
      enemyData.type === "snakeBody" ||
      enemyData.type === "snakeTail"

    if (enemyData.isBossPart) {
      if (!this.options.bossSystem.syncPart(sprite, enemyData)) {
        return false
      }
    }

    const scrollFactor = isBoss ? 0 : SCENE_TUNING.enemyScrollFactor
    const scrollCompensation = gameData.scrollSpeed * scrollFactor
    const enemyDef = RUNTIME_ENEMY_DEFINITIONS[enemyData.type]
    const enemyRuntime = sprite.getData("enemyRuntime") as EnemyRuntime

    this.updateHoverVelocity(enemyData, delta)
    const stasisScale = this.updateStasisSlow(enemyData, delta)

    sprite.x +=
      enemyData.vx * stasisScale * (delta / 16) -
      scrollCompensation * (delta / 16)

    sprite.y += enemyData.vy * stasisScale * (delta / 16)

    enemyRuntime.update({
      time,
      delta: delta * stasisScale,
      playerX: dogSprite.x,
      playerY: dogSprite.y,
      scrollCompensation,
      scene: this.options.scene,
      enemyGroup: this.options.enemyGroup,
      expCoinGroup: this.options.expCoinGroup,
      vfx: this.options.vfx,
      enemyDef,
    })
    enemyData.x = sprite.x
    enemyData.y = sprite.y

    const isBossInstance = enemyData.bossMaxHp !== undefined
    if (
      (isBossInstance && sprite.x < -200) ||
      (sprite.x < SCENE_TUNING.enemyOffscreenCleanupX && !isBossInstance) ||
      (!isBossInstance && sprite.x > GAME_WIDTH + 200)
    ) {
      sprite.destroy()
      return false
    }

    return true
  }

  private updateHoverVelocity(enemyData: EnemyData, delta: number): void {
    if (enemyData.hoverTimer !== undefined && enemyData.hoverTimer > 0) {
      enemyData.hoverTimer -= delta
      if (enemyData.hoverTimer <= 0) {
        enemyData.hoverTimer = 0
        enemyData.vx = (enemyData.hoverVx ?? -1.0) * 0.3
      }
      return
    }

    if (
      enemyData.hoverVx !== undefined &&
      Math.abs(enemyData.vx) < Math.abs(enemyData.hoverVx)
    ) {
      const rampRate = Math.abs(enemyData.hoverVx) * (delta / 2000)
      enemyData.vx = Math.min(enemyData.vx - rampRate, enemyData.hoverVx)
    }
  }

  private updateStasisSlow(enemyData: EnemyData, delta: number): number {
    if (!enemyData.stasisSlowTimer || enemyData.stasisSlowTimer <= 0) {
      enemyData.stasisSlowTimer = 0
      enemyData.stasisSlowScale = undefined
      return 1
    }

    enemyData.stasisSlowTimer = Math.max(0, enemyData.stasisSlowTimer - delta)
    return Phaser.Math.Clamp(enemyData.stasisSlowScale ?? 1, 0.2, 1)
  }

  private updateCombat(
    sprite: GameSprite,
    enemyData: EnemyData,
    delta: number,
    dogSprite: GameSprite,
  ): void {
    enemyData.lastShot += delta

    if (
      enemyData.shootCooldown > 0 &&
      sprite.x < GAME_WIDTH - SCENE_TUNING.enemyFireActivationX &&
      enemyData.lastShot >= enemyData.shootCooldown
    ) {
      enemyData.lastShot = 0
      this.options.enemies.fireEnemyProjectile(sprite, dogSprite.x, dogSprite.y)
    }
  }

  private applyVisuals(
    sprite: GameSprite,
    enemyData: EnemyData,
    time: number,
  ): void {
    const enemyType = String(enemyData.type)
    const enemyDef = RUNTIME_ENEMY_DEFINITIONS[enemyData.type]
    const enemySize = enemyDef.spriteSize
    const displayScale = SCENE_TUNING.enemyDisplayScale

    const pulse =
      1 +
      Math.sin((time + sprite.x) / SCENE_TUNING.enemyScalePulseDivisor) *
        SCENE_TUNING.enemyScalePulseAmount
    const fishScale =
      enemyType === "submarine" ? (enemyData.fishVariantScale ?? 1) : 1

    sprite.setDisplaySize(
      enemySize.w * displayScale * pulse * fishScale,
      enemySize.h * displayScale * pulse * fishScale,
    )

    if (enemyDef.assetPath.endsWith("sheet.webp")) {
      sprite.setAngle(0)
      return
    }

    const codePulse = Math.sin((time + sprite.x * 4) / 220)
    switch (enemyType) {
      case "healer":
        sprite.setAngle(codePulse * 2.5)
        return
      case "harvester":
        sprite.setAngle(codePulse * 4)
        return
      case "router":
      case "snake":
      case "snakeHead":
      case "snakeBody":
      case "snakeTail":
      case "brain":
        return
      case "humanoid":
        sprite.setAngle(codePulse * 5)
        return
      case "kamikaze":
        sprite.setAngle(codePulse * 15)
        return
      case "gunPod":
        sprite.setAngle(codePulse * 2)
        return
      case "bomber":
        sprite.setAngle(codePulse * 6)
        return
      case "chainDrifter":
        sprite.setAngle(codePulse * 8)
        return
      default:
        sprite.setAngle(codePulse * 2)
    }
  }

  private updateMovementDebugLabel(
    sprite: GameSprite,
    enemyData: EnemyData,
  ): void {
    const label = sprite.getData(MOVEMENT_DEBUG_LABEL_DATA_KEY) as
      | Phaser.GameObjects.Text
      | undefined
    if (!label) return

    const behavior = sprite.getData(ENEMY_BEHAVIOR_SNAPSHOT_DATA_KEY) as
      | EnemyBehaviorSnapshot
      | undefined
    const runtimeState = behavior?.runtimeState
    const pattern = enemyData.movementPatternLabel ?? String(enemyData.type)
    label.setText(runtimeState ? `${pattern} / ${runtimeState}` : pattern)
    label.setPosition(sprite.x, sprite.y - sprite.displayHeight * 0.55 - 4)
    label.setVisible(sprite.active && sprite.visible)
  }
}

interface EnemyDefeatFlowOptions {
  enemyGroup: Phaser.Physics.Arcade.Group
  expCoinGroup: Phaser.Physics.Arcade.Group
  enemies: EnemySystem
  vfx: VfxManager
  economy: EconomySystem
  bossSystem: BossSystem
  getGameData: () => import("../types").GameData
  getWaveNumber: () => number
  playSfx: (key: string, volume?: number) => void
  spawnCoin: (x: number, y: number, value: number) => void
  onBossDefeated?: () => void
  createToxicZone: (x: number, y: number) => void
}

export class EnemyDefeatFlow {
  private readonly options: EnemyDefeatFlowOptions

  public constructor(options: EnemyDefeatFlowOptions) {
    this.options = options
  }

  public defeat(enemy: GameSprite): void {
    if (!enemy.active) return
    if (enemy.getData(ENEMY_DEFEAT_IN_PROGRESS_DATA_KEY)) return
    const enemyData = enemy.entityData
    if (!enemyData || enemyData.kind !== "enemy") return
    enemy.setData(ENEMY_DEFEAT_IN_PROGRESS_DATA_KEY, true)

    const gameData = this.options.getGameData()
    const waveNumber = this.options.getWaveNumber()
    const xpValue = Math.max(
      1,
      Math.round(enemyData.points * this.getEnemyXpMultiplier(waveNumber)),
    )
    const coinValue = Math.max(
      1,
      Math.floor(
        SCENE_TUNING.baseCoinDrop + waveNumber * SCENE_TUNING.coinDropWaveScale,
      ),
    )

    this.grantRewards(enemy, enemyData, xpValue, gameData)
    this.playEffects(enemy, enemyData)

    const captureX = enemy.x
    const captureY = enemy.y
    const isSnakePart = this.isSnakePart(enemyData)
    const { splitSpawned, shouldCallBossDefeated } =
      this.options.bossSystem.handleBossDefeat(enemy, enemyData)

    if (splitSpawned) {
      enemy.destroy()
      EventBus.emit(EVENT.ENEMY_SPLIT, { x: captureX, y: captureY })
      this.options.spawnCoin(captureX, captureY, coinValue)
      EventBus.emit(EVENT.ENEMY_DEFEATED)
      return
    }

    if (isSnakePart) {
      this.callBossDefeatedIfNeeded(shouldCallBossDefeated)
      enemy.destroy()
      this.options.spawnCoin(captureX, captureY, coinValue)
      EventBus.emit(EVENT.ENEMY_DEFEATED)
      return
    }

    this.callBossDefeatedIfNeeded(shouldCallBossDefeated)
    this.options.spawnCoin(enemy.x, enemy.y, coinValue)

    if (this.shouldCreateToxicZone(enemyData, enemy.x)) {
      this.options.createToxicZone(enemy.x, enemy.y)
    }

    enemy.destroy()

    EventBus.emit(EVENT.ENEMY_DEFEATED)
  }

  private grantRewards(
    enemy: GameSprite,
    enemyData: EnemyData,
    xpValue: number,
    gameData: import("../types").GameData,
  ): void {
    const baseMultiplier = gameData.player.scoreMultiplier || 1
    const passiveMultiplier =
      1 + (gameData.player.passives.scoreMultiplier ?? 0)
    const totalMultiplier = baseMultiplier * passiveMultiplier
    const scoreAward = Math.floor(enemyData.points * totalMultiplier)

    gameData.score += scoreAward
    gameData.scoreThisWave += scoreAward
    gameData.enemiesKilledThisWave++
    if (scoreAward > 0) {
      this.options.vfx.showFloatingText(
        enemy.x,
        enemy.y - 26,
        `+${scoreAward.toLocaleString()}`,
        COLOR.warningText,
        scoreAward >= 1000 ? "14px" : "12px",
      )
    }

    if (this.options.economy.grantXp(xpValue)) {
      this.options.vfx.flashScreen(COLOR.warning, 0.5, 200)
    }
  }

  private playEffects(enemy: GameSprite, enemyData: EnemyData): void {
    this.options.playSfx(AUDIO.EXPLOSION, 0.3)

    if (enemyData.type === "snake") {
      this.options.vfx.playDebrisExplosion(enemy.x, enemy.y, 2.0)
      this.options.vfx.shakeCamera(600, 0.04)
      this.options.vfx.flashScreen(COLOR.danger, 0.5, 300)
      this.options.vfx.emitExplosionBurst(
        enemy.x,
        enemy.y,
        COLOR.dangerAlert,
        2.0,
      )
    } else if (this.isSnakePart(enemyData)) {
      this.options.vfx.playDebrisExplosion(enemy.x, enemy.y, 1.4)
      this.options.vfx.emitExplosionBurst(enemy.x, enemy.y, COLOR.pink, 1.5)
    } else if (enemyData.type === "brain") {
      this.options.vfx.playDebrisExplosion(enemy.x, enemy.y, 1.6)
    } else {
      this.options.vfx.emitExplosionBurst(enemy.x, enemy.y, COLOR.orange, 1.2)
    }

    this.options.vfx.bumperEmitter.explode(
      SCENE_TUNING.defeatedEnemySparkParticles,
      enemy.x,
      enemy.y,
    )
  }

  private callBossDefeatedIfNeeded(shouldCallBossDefeated: boolean): void {
    if (shouldCallBossDefeated && this.options.onBossDefeated) {
      this.options.onBossDefeated()
    }
  }

  private shouldCreateToxicZone(enemyData: EnemyData, x: number): boolean {
    return String(enemyData.type) === "flyingAlien" && x > 200
  }

  private isSnakePart(enemyData: EnemyData): boolean {
    return (
      enemyData.type === "snakeHead" ||
      enemyData.type === "snakeBody" ||
      enemyData.type === "snakeTail"
    )
  }

  private getEnemyXpMultiplier(waveNumber: number): number {
    if (waveNumber === 1) return 0.1
    if (waveNumber <= 3) return 0.14
    if (waveNumber <= 6) return 0.12
    if (waveNumber <= 10) return 0.1
    if (waveNumber <= 15) return 0.09
    return 0.08
  }
}

interface EnemySeparationOptions {
  scene: Phaser.Scene
  enemyGroup: Phaser.Physics.Arcade.Group
}

const ZERO_DISTANCE_NUDGE = 1.5
const MAX_PUSH_SCALE = 1.2
const FRAME_NORMALIZER = 16.6

export class EnemySeparation {
  private readonly options: EnemySeparationOptions

  public constructor(options: EnemySeparationOptions) {
    this.options = options
  }

  public update(delta: number): void {
    const force = SCENE_TUNING.enemySeparationForce * (delta / FRAME_NORMALIZER)
    const radius = SCENE_TUNING.enemySeparationRadius

    this.options.scene.physics.overlap(
      this.options.enemyGroup,
      this.options.enemyGroup,
      (child1, child2) => {
        const enemyA = child1 as GameSprite
        const enemyB = child2 as GameSprite
        const dataA = enemyA.entityData
        const dataB = enemyB.entityData

        if (!this.canSeparate(dataA, dataB)) return
        const enemyDataA = dataA as EnemyData
        const enemyDataB = dataB as EnemyData

        const dx = enemyA.x - enemyB.x
        const dy = enemyA.y - enemyB.y
        const distanceSq = dx * dx + dy * dy

        if (distanceSq === 0) {
          this.nudgeApart(enemyA, enemyB)
          return
        }

        if (distanceSq >= radius * radius) return

        const distance = Math.sqrt(distanceSq)
        const overlap = radius - distance
        const maxPush = MAX_PUSH_SCALE * (delta / FRAME_NORMALIZER)
        const push = Math.min(overlap * force, maxPush)

        const unitX = dx / distance
        const unitY = dy / distance
        const pushX = unitX * push
        const pushY = unitY * push

        enemyA.x += pushX
        enemyA.y += pushY
        enemyB.x -= pushX
        enemyB.y -= pushY

        enemyDataA.x = enemyA.x
        enemyDataA.y = enemyA.y
        enemyDataB.x = enemyB.x
        enemyDataB.y = enemyB.y
      },
    )
  }

  private canSeparate(
    dataA: GameSprite["entityData"],
    dataB: GameSprite["entityData"],
  ): dataA is EnemyData {
    return !!(
      dataA &&
      dataB &&
      dataA.kind === "enemy" &&
      dataB.kind === "enemy" &&
      !dataA.isBossPart &&
      !dataB.isBossPart
    )
  }

  private nudgeApart(enemyA: GameSprite, enemyB: GameSprite): void {
    const angle = Math.random() * Math.PI * 2
    const offsetX = Math.cos(angle) * ZERO_DISTANCE_NUDGE
    const offsetY = Math.sin(angle) * ZERO_DISTANCE_NUDGE

    enemyA.x += offsetX
    enemyA.y += offsetY
    enemyB.x -= offsetX
    enemyB.y -= offsetY
  }
}

interface EnemyFlowOptions {
  scene: Phaser.Scene
  enemyGroup: Phaser.Physics.Arcade.Group
  expCoinGroup: Phaser.Physics.Arcade.Group
  enemies: EnemySystem
  vfx: VfxManager
  bumperSystem: BumperSystem
  economy: EconomySystem
  bossSystem: BossSystem
  getGameData: () => import("../types").GameData
  getDogSprite: () => GameSprite
  getWaveNumber: () => number
  playSfx: (key: string, volume?: number) => void
  spawnCoin: (x: number, y: number, value: number) => void
  onGathererEscape: () => void
  onBossDefeated?: () => void
  damagePlayer: (damage: number) => void
}

export class EnemyFlow {
  private readonly options: EnemyFlowOptions
  private readonly defeatFlow: EnemyDefeatFlow
  private readonly loop: EnemyLoop
  private readonly separation: EnemySeparation
  private readonly toxicZones: ToxicZoneSystem

  constructor(options: EnemyFlowOptions) {
    this.options = options
    this.loop = new EnemyLoop({
      scene: options.scene,
      enemyGroup: options.enemyGroup,
      expCoinGroup: options.expCoinGroup,
      enemies: options.enemies,
      vfx: options.vfx,
      bossSystem: options.bossSystem,
      getGameData: options.getGameData,
      getDogSprite: options.getDogSprite,
    })
    this.separation = new EnemySeparation({
      scene: options.scene,
      enemyGroup: options.enemyGroup,
    })
    this.toxicZones = new ToxicZoneSystem({
      scene: options.scene,
      getDogSprite: options.getDogSprite,
      damagePlayer: options.damagePlayer,
    })
    this.defeatFlow = new EnemyDefeatFlow({
      enemyGroup: options.enemyGroup,
      expCoinGroup: options.expCoinGroup,
      enemies: options.enemies,
      vfx: options.vfx,
      economy: options.economy,
      bossSystem: options.bossSystem,
      getGameData: options.getGameData,
      getWaveNumber: options.getWaveNumber,
      playSfx: options.playSfx,
      spawnCoin: options.spawnCoin,
      onBossDefeated: options.onBossDefeated,
      createToxicZone: (x, y) => this.createToxicZone(x, y),
    })
  }

  public updateToxicZones(_time: number, delta: number): void {
    this.toxicZones.update(delta)
  }

  public createToxicZone(x: number, y: number): void {
    this.toxicZones.create(x, y)
  }

  public resetBossPhaseTracking(): void {
    this.options.bossSystem.resetPhaseTracking()
  }

  public updateEnemies(time: number, delta: number): void {
    this.loop.update(time, delta)
    this.separation.update(delta)
  }

  public defeatEnemy(enemy: GameSprite): void {
    this.defeatFlow.defeat(enemy)
  }
}

export const ENEMY_BEHAVIOR_STATE_DATA_KEY = "enemyBehaviorState"
export const ENEMY_BEHAVIOR_SNAPSHOT_DATA_KEY = "enemyBehavior"
export const ENEMY_ANIMATION_STATE_DATA_KEY = "enemyAnimationState"

export type EnemyAnimationState =
  | "idle"
  | "spawning"
  | "flying"
  | "patrolling"
  | "tracking"
  | "focusing"
  | "attacking"
  | "retreating"
  | "diving"
  | "panicked"
  | "hovering"
  | "stunned"
  | "damaged"
  | "dying"

export type EnemyBehaviorState =
  | EnemyStateValue
  | GathererPhase
  | HealerPhase
  | DiverPhase
  | SnakeFlightPhase
  | BrainBossState["brainPhase"]

type EnemyStateValue =
  | "idle"
  | "spawning"
  | "flying"
  | "patrolling"
  | "tracking"
  | "focusing"
  | "attacking"
  | "retreating"
  | "diving"
  | "panicked"
  | "hovering"
  | "stunned"
  | "dying"

export interface EnemyBehaviorSnapshot {
  state: EnemyBehaviorState
  animationState?: EnemyAnimationState
  runtimeState?: string
  changedAt?: number
}

function ensureDataManager(sprite: GameSprite): void {
  if (!sprite.data) {
    sprite.setDataEnabled()
  }
}

function transitionEnemyBehaviorState<T extends EnemyBehaviorState>(
  sprite: GameSprite,
  currentState: T,
  nextState: T,
  setState: (state: T) => void,
): void {
  if (currentState === nextState) return
  setState(nextState)
  setEnemyBehaviorState(sprite, nextState)
}

export function setEnemyBehaviorSnapshot(
  sprite: GameSprite,
  snapshot: EnemyBehaviorSnapshot,
): void {
  ensureDataManager(sprite)

  const currentState = sprite.getData(ENEMY_BEHAVIOR_STATE_DATA_KEY) as
    | EnemyBehaviorState
    | undefined
  const currentAnimationState = sprite.getData(
    ENEMY_ANIMATION_STATE_DATA_KEY,
  ) as EnemyAnimationState | undefined
  const currentSnapshot = sprite.getData(ENEMY_BEHAVIOR_SNAPSHOT_DATA_KEY) as
    | EnemyBehaviorSnapshot
    | undefined

  if (
    currentState === snapshot.state &&
    currentAnimationState === snapshot.animationState &&
    currentSnapshot?.runtimeState === snapshot.runtimeState &&
    currentSnapshot?.changedAt === snapshot.changedAt
  ) {
    return
  }

  sprite.setData(ENEMY_BEHAVIOR_STATE_DATA_KEY, snapshot.state)
  sprite.setData(ENEMY_ANIMATION_STATE_DATA_KEY, snapshot.animationState)
  sprite.setData(ENEMY_BEHAVIOR_SNAPSHOT_DATA_KEY, snapshot)
}

export function setEnemyBehaviorState(
  sprite: GameSprite,
  nextState: EnemyBehaviorState,
  metadata: Omit<EnemyBehaviorSnapshot, "state"> = {},
): void {
  setEnemyBehaviorSnapshot(sprite, {
    state: nextState,
    ...metadata,
  })
}

export function transitionDiverPhase(
  sprite: GameSprite,
  diverState: DiverState,
  nextPhase: DiverPhase,
): void {
  transitionEnemyBehaviorState(
    sprite,
    diverState.divePhase,
    nextPhase,
    (state) => {
      diverState.divePhase = state
    },
  )
}

export function transitionSnakeFlightPhase(
  sprite: GameSprite,
  snakeState: SnakePartState,
  nextPhase: SnakeFlightPhase,
): void {
  transitionEnemyBehaviorState(
    sprite,
    snakeState.flightPhase,
    nextPhase,
    (state) => {
      snakeState.flightPhase = state
    },
  )
}

export function transitionBrainBossPhase(
  sprite: GameSprite,
  brainState: BrainBossState,
  nextPhase: BrainBossState["brainPhase"],
): void {
  transitionEnemyBehaviorState(
    sprite,
    brainState.brainPhase,
    nextPhase,
    (state) => {
      brainState.brainPhase = state
    },
  )
}

export interface EnemyUpdateContext {
  time: number
  delta: number
  playerX: number
  playerY: number
  scrollCompensation: number
  scene: Phaser.Scene
  enemyGroup: Phaser.Physics.Arcade.Group
  expCoinGroup: Phaser.Physics.Arcade.Group
  vfx: VfxManager
  enemyDef?: RuntimeEnemyDefinition
}

export abstract class BaseEnemyRuntime {
  protected readonly sprite: GameSprite
  protected readonly enemyData: EnemyData
  private baseMovement?: EnemyMovementHandler

  public constructor(sprite: GameSprite, enemyData: EnemyData) {
    this.sprite = sprite
    this.enemyData = enemyData
  }

  public onSpawn(): void {}

  public abstract update(context: EnemyUpdateContext): void

  public setBaseMovement(movement: EnemyMovementHandler): void {
    this.baseMovement = movement
  }

  public canFire(): boolean {
    return true
  }

  public onFire(): void {}

  public onHeroCollision(): "damage-only" | "damage-and-defeat" | null {
    return null
  }

  public destroy(): void {}

  protected setBehaviorState(state: EnemyBehaviorState): void {
    setEnemyBehaviorState(this.sprite, state)
  }

  protected setBehaviorSnapshot(snapshot: EnemyBehaviorSnapshot): void {
    setEnemyBehaviorSnapshot(this.sprite, snapshot)
  }

  protected getMaxHp(): number {
    return this.enemyData.maxHp ?? this.enemyData.hp
  }

  protected applyBaseMovement(context: EnemyUpdateContext): void {
    this.baseMovement?.(
      this.sprite,
      context.time,
      context.delta,
      context.playerY,
      context.playerX,
      context.scrollCompensation,
    )
  }
}

export interface EnemyStateContext<TState extends string>
  extends EnemyUpdateContext {
  sprite: GameSprite
  enemyData: EnemyData
  stateName: TState
  stateTime: number
  maxHp: number
  hpRatio: number
}

export interface EnemyState<TState extends string>
  extends RuntimeState<TState, EnemyStateContext<TState>> {
  behaviorState: EnemyBehaviorState
  animationState?: EnemyAnimationState
  canFire?: boolean
  nextStateOnFire?: TState
  onDamage?(context: EnemyStateContext<TState>, amount: number): TState | null
}

export abstract class StatefulEnemyRuntime<
  TState extends string,
> extends BaseEnemyRuntime {
  private readonly fsm: StatefulRuntime<TState, EnemyStateContext<TState>>
  private readonly enemyStates: Map<TState, EnemyState<TState>>
  private worldBase: Omit<EnemyUpdateContext, "time" | "delta"> | null = null

  protected constructor(
    sprite: GameSprite,
    enemyData: EnemyData,
    initialState: TState,
    states: EnemyState<TState>[],
  ) {
    super(sprite, enemyData)
    this.enemyStates = new Map(states.map((s) => [s.name, s]))
    this.fsm = new StatefulRuntime<TState, EnemyStateContext<TState>>(
      initialState,
      states,
      (time, delta, stateName, stateTime) =>
        this.buildEnemyCtx(time, delta, stateName, stateTime),
      (state, ctx) => this.onEnemyStateEntered(state, ctx),
    )
  }

  public override onSpawn(): void {}

  public override destroy(): void {
    super.destroy()
  }

  public update(context: EnemyUpdateContext): void {
    const { time: _t, delta: _d, ...world } = context
    this.worldBase = world

    if ((this.enemyData.lastDamageReceived ?? 0) > 0) {
      const damage = this.enemyData.lastDamageReceived ?? 0
      this.enemyData.lastDamageReceived = 0
      const lastCtx = this.fsm.getLastCtx()
      const def = this.enemyStates.get(this.fsm.getStateName())
      if (def?.onDamage && lastCtx) {
        const nextState = def.onDamage(lastCtx, damage)
        if (nextState) {
          this.fsm.forceTransition(nextState, lastCtx)
          return
        }
      }
    }

    this.fsm.tick(context.time, context.delta)
  }

  public override canFire(): boolean {
    return this.enemyStates.get(this.fsm.getStateName())?.canFire ?? true
  }

  public override onFire(): void {
    const def = this.enemyStates.get(this.fsm.getStateName())
    if (!def?.nextStateOnFire) return
    this.fsm.forceTransition(def.nextStateOnFire, this.fsm.getLastCtx())
  }

  protected getStateName(): TState {
    return this.fsm.getStateName()
  }

  protected transitionTo(nextState: TState): void {
    this.fsm.queueTransition(nextState)
  }

  protected transitionToState(
    nextState: TState,
    sourceContext?: EnemyStateContext<TState>,
  ): void {
    this.fsm.forceTransition(nextState, sourceContext ?? this.fsm.getLastCtx())
  }

  protected getStateHpRatio(): number {
    return this.enemyData.hp / this.getMaxHp()
  }

  private buildEnemyCtx(
    time: number,
    delta: number,
    stateName: TState,
    stateTime: number,
  ): EnemyStateContext<TState> {
    const world = this.worldBase ?? {
      playerX: this.sprite.x,
      playerY: this.sprite.y,
      scrollCompensation: 0,
      scene: this.sprite.scene,
      enemyGroup: this.sprite.scene.physics.add.group(),
      expCoinGroup: this.sprite.scene.physics.add.group(),
      vfx: {} as VfxManager,
      enemyDef: undefined,
    }
    const maxHp = this.getMaxHp()
    return {
      time,
      delta,
      ...world,
      sprite: this.sprite,
      enemyData: this.enemyData,
      stateName,
      stateTime,
      maxHp,
      hpRatio: this.enemyData.hp / maxHp,
    }
  }

  private onEnemyStateEntered(
    state: TState,
    ctx: EnemyStateContext<TState>,
  ): void {
    const def = this.enemyStates.get(state)
    if (!def) return
    this.setBehaviorSnapshot({
      state: def.behaviorState,
      animationState: def.animationState,
      runtimeState: def.name,
      changedAt: ctx.time,
    })
  }
}

export type { RuntimeStateContext }
